import { each, isEmpty, map, omit, pluck } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { Repository } from '@bingads-webui-campaign-legacy/adextension-image/service/repository';
import summaryDataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';

export const modelToViewModelForDisclaimer = ({
  disclaimerAssociations,
}) => {
  let selectedDisclaimer = null;
  if (disclaimerAssociations && disclaimerAssociations.length > 0) {
    const adExtension = disclaimerAssociations[0].AdExtension;
    selectedDisclaimer = {
      Id: adExtension.Id,
      Name: adExtension.Name,
    };
  }

  return {
    disclaimer: {
      originalSelectedDisclaimer: selectedDisclaimer,
      selectedDisclaimer,
    },
  };
};

export const viewModelToBulkModelForDisclaimer = (campaignViewModel) => {
  let adExtensionAssociations = null;

  if (campaignViewModel && campaignViewModel.disclaimer && campaignViewModel.disclaimer.adExtensionAssociations) {
    adExtensionAssociations = campaignViewModel.disclaimer.adExtensionAssociations.map(adExtensionAssociation => ({
      ...omit(adExtensionAssociation, ['Client Id', 'Name', 'CampaignName']),
      Campaign: {
        Id: campaignViewModel.Id,
      },
    }));

    return { AdExtensionAssociations: adExtensionAssociations };
  }

  return null;
};

export const bulkModelToViewModelForDisclaimer = ({
  campaignModel,
  originalCampaignModel,
}) => {
  if (campaignModel.AdExtensionAssociations && campaignModel.AdExtensionAssociations.length > 0) {
    const activeAssociations = campaignModel.AdExtensionAssociations.filter(association => association.Status === 'Active');
    if (activeAssociations && activeAssociations.length > 0) {
      const selectedDisclaimer = activeAssociations[0];
      const originalSelectedDisclaimer = originalCampaignModel.disclaimer.selectedDisclaimer;
      if (selectedDisclaimer.Id === originalSelectedDisclaimer.Id) {
        return {
          disclaimer: {
            selectedDisclaimer: {
              Id: originalSelectedDisclaimer.Id,
              Name: originalSelectedDisclaimer.Name,
            },
            adExtensionAssociations: campaignModel.AdExtensionAssociations,
          },
        };
      }
    }
  }

  return {
    disclaimer: null,
  };
};

// the magic date is for bidata, but useless for this request
const magicDate = '2024-10-10';

export const loadDisclaimerAssociations = ({ cid, aid, campaignId }) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdExtensionAssociations
    .get({
      filter: {
        where: 'AdExtension/Status eq Enum.AdExtensionStatus\'Active\'',
      },
      $count: true,
      $top: 1,
      $expand: 'AdExtension',
      adExtensionType: 'DisclaimerAdExtension',
      startdate: magicDate,
      enddate: magicDate,
      // disable cache to ensure data refreshed
      cache: Date.now(),
    })
    .toPromise();

const imageExtensionsRepository = new Repository({
  schema: window.globalLegacySchema.get('ImageAdExtension'),
  afterChangeComitted: () => {
    summaryDataContextManager.updateContext(['AdGroupAdExtensionsGrid']);
  },
});
imageExtensionsRepository.association.createRequestFactory('adgroup');

export const saveImageExtensions = ({
  campaignId,
  adGroupId,
  // adId,
  imageExtensionChanges,
}, {
  i18n,
  // activityLogger,
  // scenario,
  // perfMarker,
}) => {
  // logActivity('Check image extensions');

  const defaultParams = {
    where: {
      campaignId: Number(campaignId),
      adGroupId: Number(adGroupId),
    },
  };

  if (isEmpty(imageExtensionChanges) ||
    (isEmpty(imageExtensionChanges.added) &&
      isEmpty(imageExtensionChanges.removed) &&
      isEmpty(imageExtensionChanges.updated))) {
    return Promise.resolve(true);
  }

  // const saveImgsExtPerfMarker = perfMarker.createChild('Process_ImageExtensions_Success');
  // saveImgsExtPerfMarker.willInit();

  const createImageExtensionsPromise = map(imageExtensionChanges.added, ext => imageExtensionsRepository.extension.create(ext));

  return Promise.all(createImageExtensionsPromise)
    .then((newExtensions) => {
      const newExtensionIds = pluck(newExtensions, 'Id');

      each(imageExtensionChanges.added, (item, index) => {
        // eslint-disable-next-line no-param-reassign
        item.Id = newExtensionIds[index];
      });

      let deletePromise = Promise.resolve(true);
      if (!isEmpty(imageExtensionChanges.removed)) {
        deletePromise = imageExtensionsRepository.association.destroyAll({
          where: {
            id: { in: pluck(imageExtensionChanges.removed, 'Id') },
            ...defaultParams.where,
          },
        });
      }

      return deletePromise
        .then(() => {
          const params = {
            where: {
              id: { in: newExtensionIds },
              ...defaultParams.where,
            },
          };

          const updatePromise = map(imageExtensionChanges.updated, ext => imageExtensionsRepository.extension.update(ext));
          return Promise.all(updatePromise).then(() => imageExtensionsRepository.association.updateAll(null, params));
        });
    })
    .then(() => {
      // logActivity('Updated image extension successfully');
    })
    .catch(() => {
      // do nothing, so that this is non-blocking
      // logActivity(`Updated image extension with failures, err: ${JSON.stringify(error)}`);

      const pageName = `<b>${i18n.getString(_TL_('Ads & extensions'))}</b>`;

      notificationApi.showWarning(i18n.getString(
        _TL_('There were errors when processing your image extension updates. To ensure that all changes are up to date or to manually create a new image extension, review your image extensions in the {{page}} page.'),
        {
          page: pageName,
        }
      ));

      return Promise.resolve(true);
    })
    .finally(() => {
      // saveImgsExtPerfMarker.done();
      // logImageExtensionUpdates({
      //   adId,
      //   currentItems: imageExtensionChanges.current,
      //   createdExtModels: imageExtensionChanges.added,
      //   removedExtModels: imageExtensionChanges.removed,
      //   updatedExtModels: imageExtensionChanges.updated,
      //   scope: defaultParams.where,
      // });
    });
};

export const loadExtensionsAssociations = ({
  cid,
  aid,
  campaignId,
  adExtensionTypes = [],
  top = 1000, // get all, 20 max for each type
}) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdExtensionAssociations['Default.GridData']
    .$makeCall({
      query: {
        top,
        filter: { where: "(AdExtension/Status eq Enum.AdExtensionStatus'Active')" },
        select: ['AdExtension'],
      },
      payload: {
        gridDataParameters: {
          DateRange: {
            // Just want to get extension data and don't care BI data, so pass dummy date ranges.
            StartDate: magicDate,
            EndDate: magicDate,
          },
        },
        associationType: 'Campaign',
        adExtensionTypes,
      },
    })
    .toPromise();
